/**
 * warbandSlice.ts
 *
 * Zustand slice for warband domain data.
 * Enforces public-PDF rules (item caps, soldier caps) while keeping
 * paid-rulebook-dependent constraints user-configurable.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist } from 'zustand/middleware';

import type {
  Warband,
  Caster,
  CasterStats,
  Soldier,
  Item,
  RulesConfig,
  SoldierMetaPatch,
  WarbandExport,
  WarbandResult,
  SoldierTemplateId,
} from './types';
import { SOLDIER_TEMPLATES, isValidTemplateId } from './soldierTemplates';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Current schema version for export/import migrations */
export const SCHEMA_VERSION = 1;

/** Maximum items for Wizard (from Wizard Sheet 2e) */
export const WIZARD_ITEM_CAP = 5;

/** Maximum items for Apprentice (from Wizard Sheet 2e) */
export const APPRENTICE_ITEM_CAP = 4;

/** Maximum total soldiers in warband */
export const MAX_SOLDIERS = 8;

/** Maximum specialists in warband */
export const MAX_SPECIALISTS = 4;

/** Starting treasury gold crowns */
export const STARTING_TREASURY_GC = 400;

/** Cost to hire an apprentice */
export const APPRENTICE_COST_GC = 100;

/** localStorage key for persistence */
const STORAGE_KEY = 'fwbt.warband.v1';

/** Default wizard stats */
export const DEFAULT_WIZARD_STATS: CasterStats = {
  move: 6,
  fight: 2,
  shoot: 0,
  armor: 10,
  will: 4,
};

/** Default wizard health */
export const DEFAULT_WIZARD_HEALTH = 14;

/** Default apprentice health */
export const DEFAULT_APPRENTICE_HEALTH = 12;

// ---------------------------------------------------------------------------
// Apprentice Stats Calculation
// ---------------------------------------------------------------------------

/**
 * Calculate apprentice stats from wizard stats.
 * Apprentice has -2 to all stats EXCEPT Move (which stays the same).
 */
export function calculateApprenticeStats(wizardStats: CasterStats): CasterStats {
  return {
    move: wizardStats.move, // Move stays the same
    fight: wizardStats.fight - 2,
    shoot: wizardStats.shoot - 2,
    armor: wizardStats.armor - 2,
    will: wizardStats.will - 2,
  };
}

// ---------------------------------------------------------------------------
// Initial State
// ---------------------------------------------------------------------------

/**
 * Creates a default wizard caster.
 */
function createDefaultWizard(): Caster {
  return {
    name: '',
    school: 'Elementalist',
    level: 0,
    stats: { ...DEFAULT_WIZARD_STATS },
    items: [],
    maxItems: WIZARD_ITEM_CAP,
    health: { max: DEFAULT_WIZARD_HEALTH, current: DEFAULT_WIZARD_HEALTH },
    xp: 0,
    notes: '',
  };
}

/**
 * Creates the initial warband state.
 * Starting treasury is 400gc per standard Frostgrave rules.
 */
function createInitialWarband(): Warband {
  return {
    wizard: createDefaultWizard(),
    apprentice: undefined,
    soldiers: [],
    knownSpells: [],
    treasuryGC: STARTING_TREASURY_GC,
    vault: [],
    base: undefined,
    baseNotes: undefined,
    rulesConfig: {},
  };
}

// ---------------------------------------------------------------------------
// Guard Functions
// ---------------------------------------------------------------------------

/**
 * Check if adding an item to a caster would exceed the cap.
 * @param caster - The caster to check
 * @returns true if adding an item is allowed
 */
export function canAddItemToCaster(caster: Caster): boolean {
  return caster.items.length < caster.maxItems;
}

/**
 * Count the number of specialists in the soldier list.
 */
function countSpecialists(soldiers: Soldier[]): number {
  return soldiers.filter((s) => {
    const template = SOLDIER_TEMPLATES[s.templateId];
    return template?.isSpecialist ?? false;
  }).length;
}

/**
 * Check if adding a soldier is allowed based on caps.
 * @param soldiers - Current soldier list
 * @param templateId - Template of soldier to add
 * @param rulesConfig - Optional user-configured rules
 * @returns Result indicating success or specific error
 */
export function canAddSoldier(
  soldiers: Soldier[],
  templateId: SoldierTemplateId,
  rulesConfig?: RulesConfig
): WarbandResult<true> {
  const maxSoldiers = rulesConfig?.soldierLimit ?? MAX_SOLDIERS;
  const maxSpecialists = rulesConfig?.specialistLimit ?? MAX_SPECIALISTS;

  // Check total soldier cap
  if (soldiers.length >= maxSoldiers) {
    return {
      success: false,
      error: 'SOLDIER_CAP_EXCEEDED',
      message: `Cannot add soldier: maximum of ${maxSoldiers} soldiers reached`,
    };
  }

  // Check specialist cap if this is a specialist
  const template = SOLDIER_TEMPLATES[templateId];
  if (template?.isSpecialist) {
    const currentSpecialists = countSpecialists(soldiers);
    if (currentSpecialists >= maxSpecialists) {
      return {
        success: false,
        error: 'SPECIALIST_CAP_EXCEEDED',
        message: `Cannot add specialist: maximum of ${maxSpecialists} specialists reached`,
      };
    }
  }

  return { success: true, data: true };
}

// ---------------------------------------------------------------------------
// ID Generation
// ---------------------------------------------------------------------------

/**
 * Generate a unique ID for soldiers and items.
 * Uses timestamp + random suffix for uniqueness.
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

// ---------------------------------------------------------------------------
// Store Interface
// ---------------------------------------------------------------------------

interface WarbandState extends Warband {
  // ---------------------------------------------------------------------------
  // Caster Actions
  // ---------------------------------------------------------------------------
  setWizard: (payload: Caster) => WarbandResult<Caster>;
  setApprentice: (payload?: Caster) => WarbandResult<Caster | undefined>;

  // ---------------------------------------------------------------------------
  // Soldier Actions
  // ---------------------------------------------------------------------------
  addSoldier: (
    templateId: SoldierTemplateId,
    name?: string,
    notes?: string,
    tags?: string[]
  ) => WarbandResult<Soldier>;
  updateSoldierMeta: (id: string, patch: SoldierMetaPatch) => WarbandResult<Soldier>;
  removeSoldier: (id: string) => WarbandResult<boolean>;

  // ---------------------------------------------------------------------------
  // Spell Actions
  // ---------------------------------------------------------------------------
  attachSpell: (spellId: string) => WarbandResult<string[]>;
  detachSpell: (spellId: string) => WarbandResult<string[]>;

  // ---------------------------------------------------------------------------
  // Treasury & Vault Actions
  // ---------------------------------------------------------------------------
  setTreasuryGC: (value: number) => void;
  addVaultItem: (item: Omit<Item, 'id'>) => WarbandResult<Item>;
  removeVaultItem: (id: string) => WarbandResult<boolean>;

  // ---------------------------------------------------------------------------
  // Base Actions
  // ---------------------------------------------------------------------------
  setBase: (name?: string, notes?: string) => void;

  // ---------------------------------------------------------------------------
  // Rules Config Actions
  // ---------------------------------------------------------------------------
  setRulesConfig: (cfg: RulesConfig) => void;

  // ---------------------------------------------------------------------------
  // Import/Export Actions
  // ---------------------------------------------------------------------------
  load: (state: Warband, version: number) => WarbandResult<Warband>;
  exportWarband: () => WarbandExport;

  // ---------------------------------------------------------------------------
  // Reset Action
  // ---------------------------------------------------------------------------
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Store Creation
// ---------------------------------------------------------------------------

export const useWarbandStore = create<WarbandState>()(
  persist(
    immer((set, get) => ({
      // Initial state
      ...createInitialWarband(),

      // -----------------------------------------------------------------------
      // Caster Actions
      // -----------------------------------------------------------------------

      setWizard: (payload) => {
        // Validate item cap
        if (payload.items.length > WIZARD_ITEM_CAP) {
          return {
            success: false,
            error: 'ITEM_CAP_EXCEEDED',
            message: `Wizard cannot have more than ${WIZARD_ITEM_CAP} items`,
          };
        }

        // Ensure maxItems is correct for wizard
        const wizard: Caster = {
          ...payload,
          maxItems: WIZARD_ITEM_CAP,
        };

        set((state) => {
          state.wizard = wizard;
        });

        return { success: true, data: wizard };
      },

      setApprentice: (payload) => {
        const state = get();
        const hadApprentice = state.apprentice !== undefined;

        if (payload === undefined) {
          // Removing apprentice - refund cost
          set((state) => {
            state.apprentice = undefined;
            if (hadApprentice) {
              state.treasuryGC += APPRENTICE_COST_GC;
            }
          });
          return { success: true, data: undefined };
        }

        // Validate item cap
        if (payload.items.length > APPRENTICE_ITEM_CAP) {
          return {
            success: false,
            error: 'ITEM_CAP_EXCEEDED',
            message: `Apprentice cannot have more than ${APPRENTICE_ITEM_CAP} items`,
          };
        }

        // If hiring a new apprentice (not updating existing), check and deduct cost
        if (!hadApprentice) {
          if (state.treasuryGC < APPRENTICE_COST_GC) {
            return {
              success: false,
              error: 'ITEM_CAP_EXCEEDED',
              message: `Not enough gold to hire apprentice: need ${APPRENTICE_COST_GC}gc, have ${state.treasuryGC}gc`,
            };
          }
        }

        // Ensure maxItems is correct for apprentice
        const apprentice: Caster = {
          ...payload,
          maxItems: APPRENTICE_ITEM_CAP,
        };

        set((state) => {
          // Deduct cost only if hiring new (not updating)
          if (!hadApprentice) {
            state.treasuryGC -= APPRENTICE_COST_GC;
          }
          state.apprentice = apprentice;
        });

        return { success: true, data: apprentice };
      },

      // -----------------------------------------------------------------------
      // Soldier Actions
      // -----------------------------------------------------------------------

      addSoldier: (templateId, name, notes, tags) => {
        // Validate template
        if (!isValidTemplateId(templateId)) {
          return {
            success: false,
            error: 'INVALID_TEMPLATE',
            message: `Invalid soldier template: ${templateId}`,
          };
        }

        const state = get();
        const canAdd = canAddSoldier(state.soldiers, templateId, state.rulesConfig);

        if (!canAdd.success) {
          return canAdd;
        }

        // Check if there's enough gold
        const template = SOLDIER_TEMPLATES[templateId];
        const cost = template?.cost ?? 0;
        if (state.treasuryGC < cost) {
          return {
            success: false,
            error: 'SOLDIER_CAP_EXCEEDED',
            message: `Not enough gold: need ${cost}gc, have ${state.treasuryGC}gc`,
          };
        }

        const soldier: Soldier = {
          id: generateId(),
          templateId,
          name,
          notes,
          tags,
        };

        set((state) => {
          state.soldiers.push(soldier);
          // Deduct cost from treasury
          state.treasuryGC -= cost;
        });

        return { success: true, data: soldier };
      },

      updateSoldierMeta: (id, patch) => {
        const state = get();
        const index = state.soldiers.findIndex((s) => s.id === id);

        if (index === -1) {
          return {
            success: false,
            error: 'SOLDIER_NOT_FOUND',
            message: `Soldier with id ${id} not found`,
          };
        }

        let updatedSoldier: Soldier | undefined;

        set((state) => {
          const soldier = state.soldiers[index];
          if (soldier) {
            if (patch.name !== undefined) soldier.name = patch.name;
            if (patch.notes !== undefined) soldier.notes = patch.notes;
            if (patch.tags !== undefined) soldier.tags = patch.tags;
            updatedSoldier = {
              id: soldier.id,
              templateId: soldier.templateId,
              name: soldier.name,
              notes: soldier.notes,
              tags: soldier.tags,
            };
          }
        });

        if (!updatedSoldier) {
          return {
            success: false,
            error: 'SOLDIER_NOT_FOUND',
            message: `Soldier with id ${id} not found`,
          };
        }

        return { success: true, data: updatedSoldier };
      },

      removeSoldier: (id) => {
        const state = get();
        const index = state.soldiers.findIndex((s) => s.id === id);

        if (index === -1) {
          return {
            success: false,
            error: 'SOLDIER_NOT_FOUND',
            message: `Soldier with id ${id} not found`,
          };
        }

        // Get the soldier's cost for refund
        const soldier = state.soldiers[index];
        const template = soldier ? SOLDIER_TEMPLATES[soldier.templateId] : undefined;
        const refund = template?.cost ?? 0;

        set((state) => {
          state.soldiers.splice(index, 1);
          // Refund cost to treasury
          state.treasuryGC += refund;
        });

        return { success: true, data: true };
      },

      // -----------------------------------------------------------------------
      // Spell Actions
      // -----------------------------------------------------------------------

      attachSpell: (spellId) => {
        const state = get();

        // Check for duplicates
        if (state.knownSpells.includes(spellId)) {
          return {
            success: false,
            error: 'DUPLICATE_SPELL',
            message: `Spell ${spellId} is already attached`,
          };
        }

        // Note: Validation that spellId exists in spells.json should be done
        // by the caller (UI/validation layer) before calling this action.
        // This slice only stores IDs; it doesn't have access to spells.json.

        set((state) => {
          state.knownSpells.push(spellId);
        });

        return { success: true, data: [...get().knownSpells] };
      },

      detachSpell: (spellId) => {
        const state = get();
        const index = state.knownSpells.indexOf(spellId);

        if (index === -1) {
          // No-op if spell not attached (per SPEC: "detaching non-existent id is no-op or guarded")
          return { success: true, data: [...state.knownSpells] };
        }

        set((state) => {
          state.knownSpells.splice(index, 1);
        });

        return { success: true, data: [...get().knownSpells] };
      },

      // -----------------------------------------------------------------------
      // Treasury & Vault Actions
      // -----------------------------------------------------------------------

      setTreasuryGC: (value) => {
        // Clamp to non-negative
        const clamped = Math.max(0, value);
        set((state) => {
          state.treasuryGC = clamped;
        });
      },

      addVaultItem: (item) => {
        const newItem: Item = {
          id: generateId(),
          ...item,
        };

        set((state) => {
          state.vault.push(newItem);
        });

        return { success: true, data: newItem };
      },

      removeVaultItem: (id) => {
        const state = get();
        const index = state.vault.findIndex((item) => item.id === id);

        if (index === -1) {
          return {
            success: false,
            error: 'ITEM_NOT_FOUND',
            message: `Vault item with id ${id} not found`,
          };
        }

        set((state) => {
          state.vault.splice(index, 1);
        });

        return { success: true, data: true };
      },

      // -----------------------------------------------------------------------
      // Base Actions
      // -----------------------------------------------------------------------

      setBase: (name, notes) => {
        set((state) => {
          state.base = name;
          state.baseNotes = notes;
        });
      },

      // -----------------------------------------------------------------------
      // Rules Config Actions
      // -----------------------------------------------------------------------

      setRulesConfig: (cfg) => {
        set((state) => {
          state.rulesConfig = { ...state.rulesConfig, ...cfg };
        });
      },

      // -----------------------------------------------------------------------
      // Import/Export Actions
      // -----------------------------------------------------------------------

      load: (warband, version) => {
        // Basic validation
        if (!warband || !warband.wizard) {
          return {
            success: false,
            error: 'INVALID_TEMPLATE',
            message: 'Invalid warband data: missing wizard',
          };
        }

        // TODO: Add migration logic when schema changes
        // For now, we only support version 1
        if (version > SCHEMA_VERSION) {
          return {
            success: false,
            error: 'INVALID_TEMPLATE',
            message: `Unsupported schema version: ${version}. Maximum supported: ${SCHEMA_VERSION}`,
          };
        }

        // Ensure wizard has all required fields including stats (migration for old data)
        const wizardStats = warband.wizard.stats ?? { ...DEFAULT_WIZARD_STATS };
        const wizard: Caster = {
          ...warband.wizard,
          stats: wizardStats,
          maxItems: WIZARD_ITEM_CAP,
        };

        // Ensure apprentice has all required fields if present
        const apprentice = warband.apprentice
          ? {
              ...warband.apprentice,
              stats: warband.apprentice.stats ?? calculateApprenticeStats(wizardStats),
              maxItems: APPRENTICE_ITEM_CAP as 4,
            }
          : undefined;

        const loadedWarband: Warband = {
          ...warband,
          wizard,
          apprentice,
          // Ensure arrays exist
          soldiers: warband.soldiers ?? [],
          knownSpells: warband.knownSpells ?? [],
          vault: warband.vault ?? [],
          // Ensure treasury is non-negative
          treasuryGC: Math.max(0, warband.treasuryGC ?? 0),
          rulesConfig: warband.rulesConfig ?? {},
        };

        set(() => loadedWarband);

        return { success: true, data: loadedWarband };
      },

      exportWarband: () => {
        const state = get();
        const warband: Warband = {
          wizard: state.wizard,
          apprentice: state.apprentice,
          soldiers: state.soldiers,
          knownSpells: state.knownSpells,
          treasuryGC: state.treasuryGC,
          vault: state.vault,
          base: state.base,
          baseNotes: state.baseNotes,
          rulesConfig: state.rulesConfig,
        };

        return {
          warband,
          version: SCHEMA_VERSION,
        };
      },

      // -----------------------------------------------------------------------
      // Reset Action
      // -----------------------------------------------------------------------

      reset: () => {
        set(() => createInitialWarband());
      },
    })),
    {
      name: STORAGE_KEY,
      version: 2, // Increment when schema changes
      partialize: (state) => ({
        wizard: state.wizard,
        apprentice: state.apprentice,
        soldiers: state.soldiers,
        knownSpells: state.knownSpells,
        treasuryGC: state.treasuryGC,
        vault: state.vault,
        base: state.base,
        baseNotes: state.baseNotes,
        rulesConfig: state.rulesConfig,
      }),
      // Migration function to handle old data without stats
      migrate: (persistedState: unknown, version: number) => {
        const state = persistedState as Partial<Warband>;
        
        // Migration from version 1 (or no version) to version 2: add stats
        if (version < 2) {
          // Ensure wizard has stats
          if (state.wizard && !state.wizard.stats) {
            state.wizard = {
              ...state.wizard,
              stats: { ...DEFAULT_WIZARD_STATS },
            };
          }
          
          // Ensure apprentice has stats if present
          if (state.apprentice && !state.apprentice.stats) {
            const wizardStats = state.wizard?.stats ?? DEFAULT_WIZARD_STATS;
            state.apprentice = {
              ...state.apprentice,
              stats: calculateApprenticeStats(wizardStats),
            };
          }
          
          // Ensure treasury has starting value if it was 0 (old default)
          if (state.treasuryGC === 0 && (!state.soldiers || state.soldiers.length === 0)) {
            state.treasuryGC = STARTING_TREASURY_GC;
          }
        }
        
        return state as Warband;
      },
    }
  )
);

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

/**
 * Select the wizard caster.
 */
export const selectWizard = (state: WarbandState) => state.wizard;

/**
 * Select the apprentice caster (may be undefined).
 */
export const selectApprentice = (state: WarbandState) => state.apprentice;

/**
 * Select all soldiers.
 */
export const selectSoldiers = (state: WarbandState) => state.soldiers;

/**
 * Select known spell IDs.
 */
export const selectKnownSpells = (state: WarbandState) => state.knownSpells;

/**
 * Select treasury gold crowns.
 */
export const selectTreasury = (state: WarbandState) => state.treasuryGC;

/**
 * Select vault items.
 */
export const selectVault = (state: WarbandState) => state.vault;

/**
 * Select base name.
 */
export const selectBase = (state: WarbandState) => state.base;

/**
 * Select base notes.
 */
export const selectBaseNotes = (state: WarbandState) => state.baseNotes;

/**
 * Select rules configuration.
 */
export const selectRulesConfig = (state: WarbandState) => state.rulesConfig;

// ---------------------------------------------------------------------------
// Derived Selectors (primitives only - safe for Zustand)
// ---------------------------------------------------------------------------

/**
 * Select the count of soldiers.
 */
export const selectSoldierCount = (state: WarbandState) => state.soldiers.length;

/**
 * Select the count of specialists.
 */
export const selectSpecialistCount = (state: WarbandState) =>
  countSpecialists(state.soldiers);

/**
 * Calculate the total cost of the warband.
 * Returns a primitive number - safe for Zustand.
 */
export const selectWarbandCost = (state: WarbandState) =>
  state.soldiers.reduce((total, soldier) => {
    const template = SOLDIER_TEMPLATES[soldier.templateId];
    return total + (template?.cost ?? 0);
  }, 0);

/**
 * Check if the warband can add more soldiers.
 */
export const selectCanAddSoldier = (state: WarbandState) =>
  state.soldiers.length < (state.rulesConfig.soldierLimit ?? MAX_SOLDIERS);

/**
 * Check if the warband can add more specialists.
 */
export const selectCanAddSpecialist = (state: WarbandState) =>
  countSpecialists(state.soldiers) <
  (state.rulesConfig.specialistLimit ?? MAX_SPECIALISTS);

// ---------------------------------------------------------------------------
// NOTE: For derived data that creates new objects/arrays, use useMemo in
// components instead of selectors. Example:
//
// const soldiers = useWarbandStore(selectSoldiers);
// const soldiersWithTemplates = useMemo(() =>
//   soldiers.map(s => ({ ...s, template: SOLDIER_TEMPLATES[s.templateId] })),
//   [soldiers]
// );
// ---------------------------------------------------------------------------

